import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysUser } from './queryKeys';

type UserProfilePictureResponse = {
  code: string;
  message: string;
  requestId: string;
};

/**
 * Deletes user profile picture.
 *
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/remove_profile_picture_user_profile_picture_delete
 */
export const deleteUserProfilePicture = async () => {
  const response = await vestigasApi
    .delete(ENDPOINT.USER.DELETE_PROFILE_PICTURE())
    .json<UserProfilePictureResponse>();

  return response;
};

/**
 * Custom hook for deleting the profile picture of a given user.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationDeleteUserProfilePicture = (
  options?: Parameters<typeof useMutation>[0],
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => deleteUserProfilePicture(),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysUser.getProfilePicture(),
      });
    },
    ...options,
  });
};
